/**
 * Resolving a free-text UOM claim (AI extraction, a customer's own spelling) against the real
 * `UOM` master — the same treatment `item_code` already gets before it is allowed anywhere near a
 * write. `Enquiry Item.uom` is a Frappe Link to `UOM`; a value the master doesn't recognise fails
 * the WHOLE document on save (`Could not find Row #1: UOM: NO`), not just its row.
 *
 * ---- No alias table, on purpose ----
 *
 * `"NO"` → `"Nos"` looks obvious to a human and is exactly the kind of guess this codebase
 * refuses everywhere else (see `useMailExtraction.ts`'s `verifyItemCodes`): a wrong unit silently
 * changes a quantity on a customer's order. `resolveUom`
 * only ever matches a string the master ACTUALLY contains — exact first, then case-insensitive
 * (a customer typing `"pcs"` against a master that spells it `"PCS"` is the same word, not a
 * guess). Anything else is unresolved, full stop; the purchaser picks from the real master.
 */

/** `null`/empty/whitespace-only input has nothing to resolve. */
export function resolveUom(raw: string | null | undefined, master: readonly string[]): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (trimmed === '') return null

  // Exact match first — the common case, and the only one that needs no further reasoning.
  if (master.includes(trimmed)) return trimmed

  // Case-insensitive next: SAME word, different case, not a different unit. The master's own
  // spelling/casing is returned (never the raw text) so what gets sent is exactly what the Link
  // field will accept.
  const lower = trimmed.toLowerCase()
  const ciMatch = master.find((candidate) => candidate.toLowerCase() === lower)
  if (ciMatch) return ciMatch

  /*
    Plural or singular of a master unit, last (2026-09-17): customer sheets write TUBS, PKTS,
    BTLS where the master has TUB, PKT, BTL — and PC where it has PCS. Only a trailing S (a
    trailing dot dropped first) — never a different word, and only after the exact and
    case-insensitive lookups have found nothing, so KGS stays KGS when the master has both.
  */
  const bare = lower.replace(/\.$/, '')
  // Three letters or more: "NO" → "Nos" stays refused (the test below this file pins it — that
  // one is a guess, "no" may be "number"), while TUBS → TUB and CTN → CTNS are the same word.
  if (bare.length < 3) return null
  const singular = bare.endsWith('s') && bare.length > 3 ? bare.slice(0, -1) : null
  const plural = `${bare}s`
  const byForm = master.find((candidate) => {
    const c = candidate.toLowerCase()
    return (singular !== null && c === singular) || c === plural
  })
  return byForm ?? null
}

/**
 * A raw Frappe `LinkValidationError` message — `"Could not find Row #1: UOM: NO"` — names a row
 * INDEX a purchaser cannot map to anything on screen. Parsed here so a caller with the actual
 * lines can say which one, in the app's own terms, instead of parroting the server's wording.
 *
 * Deliberately narrow: only this one documented Frappe shape is parsed. Anything else returns
 * `null` and the caller falls back to the raw message — better an unimproved-but-honest error
 * than a confidently wrong "translation" of a message this function doesn't actually recognise.
 */
export interface RowLinkErrorMatch {
  /** 1-based, matching the row order the document was written in. */
  rowNumber: number
  /** The Link fieldname the server rejected, e.g. `"UOM"`, `"item_code"`. */
  field: string
  /** The value that failed — the free text that was sent, verbatim. */
  value: string
}

const ROW_LINK_ERROR = /Could not find Row #(\d+):\s*([^:]+):\s*(.+)/i

export function parseRowLinkError(message: string): RowLinkErrorMatch | null {
  const match = ROW_LINK_ERROR.exec(message.trim())
  if (!match) return null
  const rowNumber = Number(match[1])
  if (!Number.isFinite(rowNumber) || rowNumber < 1) return null
  return { rowNumber, field: match[2].trim(), value: match[3].trim() }
}

/**
 * Turn a raw Link-validation message into something a purchaser can act on: WHICH line (by the
 * description they recognise, not a row index) and what value on it was rejected.
 *
 * `lines` must be in the SAME order the document was written in — the order the row number
 * counts against. When the message doesn't match the one documented shape, or the row number is
 * out of range, the original message is returned unchanged rather than a mistranslation.
 */
export function describeRowLinkError(message: string, lines: readonly { description: string }[]): string {
  const parsed = parseRowLinkError(message)
  if (!parsed) return message
  const line = lines[parsed.rowNumber - 1]
  const where = line ? `line ${parsed.rowNumber} ("${line.description}")` : `line ${parsed.rowNumber}`
  return `${where}: the server rejected ${parsed.field} "${parsed.value}" — it doesn't exist in the ${parsed.field} master.`
}
